import { useEffect, useState } from 'react';
import {
  Button, Chip, CircularProgress, InputAdornment, TextField,
  Tooltip, Tab, Tabs, Select, MenuItem, FormControl, InputLabel,
  Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText
} from '@mui/material';
import { ApiRequest } from 'GlobalFunctions';
import { useSnackbar } from 'notistack';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import * as XLSX from 'xlsx';

// ─── helpers ─────────────────────────────────────────────────────────────────
const estadoChip = (estado: string) => {
  if (estado === 'Activa')    return { bg: '#dcfce7', color: '#15803d', icon: <CheckCircleOutlineIcon sx={{ fontSize: 14 }} /> };
  if (estado === 'Pendiente') return { bg: '#fef9c3', color: '#92400e', icon: <HourglassEmptyIcon sx={{ fontSize: 14 }} /> };
  if (estado === 'Rechazada') return { bg: '#fee2e2', color: '#b91c1c', icon: <CancelOutlinedIcon sx={{ fontSize: 14 }} /> };
  return { bg: '#f1f5f9', color: '#64748b', icon: <CancelOutlinedIcon sx={{ fontSize: 14 }} /> }; // Inactiva
};

const SummaryCard = ({ label, value, color }: any) => (
  <div style={{
    background: '#fff', borderRadius: 12, padding: '16px 20px',
    boxShadow: '0 1px 6px rgba(0,0,0,0.08)', borderLeft: `4px solid ${color}`
  }}>
    <div style={{ fontSize: 28, fontWeight: 700, color }}>{value}</div>
    <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>{label}</div>
  </div>
);

// ─── Kinesiologists tab ────────────────────────────────────────────────────────
function KinesiologistsTab() {
  const [list, setList]       = useState<any[]>([]);
  const [fetching, setFetching] = useState(true);
  const [search, setSearch]   = useState('');
  const [filterEstado,     setFilterEstado]     = useState('Todas');
  const [filterTipo,       setFilterTipo]       = useState('Todos');
  const [filterComision,   setFilterComision]   = useState('Todos');
  const [filterResidencia, setFilterResidencia] = useState('');
  const [filterPerfil,     setFilterPerfil]     = useState('Todos');
  const { enqueueSnackbar } = useSnackbar();

  const [notifyIncompleteOpen, setNotifyIncompleteOpen] = useState(false);
  const [sendingIncomplete, setSendingIncomplete] = useState(false);
  const [incompleteSubject, setIncompleteSubject] = useState('Te faltan datos para completar tu perfil en Buscakine');
  const [incompleteBody, setIncompleteBody] = useState(
    'Estimado/a {{nombre}},\n\nLe informamos que a su perfil en Buscakine.cl le faltan los siguientes datos: {{faltantes}}. Mientras esto no se complete, su perfil no aparecerá en el buscador de pacientes.\n\nIngrese a su cuenta con su usuario y contraseña habitual; al hacerlo, el sistema le pedirá completar automáticamente los datos específicos que le faltan antes de continuar.'
  );

  const [incompleteResult, setIncompleteResult] = useState<{ sent: number; total_targets: number; failed: string[] } | null>(null);

  const [commissionDialogRow, setCommissionDialogRow] = useState<any>(null);
  const [commissionSubject, setCommissionSubject] = useState('');
  const [commissionBody, setCommissionBody] = useState('');
  const [sendingCommission, setSendingCommission] = useState(false);

  const loadList = () => {
    setFetching(true);
    ApiRequest({
      url: '/kinesiologists/report',
      method: 'GET',
      setResponse: (res: any) => {
        if (res.status === 'success') setList(res.list);
        setFetching(false);
      }
    });
  };

  useEffect(() => {
    loadList();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCommissionDialog = (row: any) => {
    setCommissionDialogRow(row);
    setCommissionSubject('Términos de derivación pendientes - Buscakine');
    setCommissionBody(
      `Hola ${row.name},\n\nSomos del equipo de Buscakine.cl. Nos percatamos de que aún no aceptas los nuevos términos de comisión por derivación ($10.000 por contacto). Esto implica que tu perfil no estará visible en el buscador hasta que aceptes estos términos.\n\nIngresa a tu cuenta para revisarlos y aceptarlos.`
    );
  };

  const sendCommissionNotice = () => {
    if (!commissionDialogRow) return;
    setSendingCommission(true);
    ApiRequest({
      url: '/kinesiologists/notify-commission',
      method: 'POST',
      body: {
        user_id: commissionDialogRow.user_id,
        subject: commissionSubject,
        body: `<p>${commissionBody.replace(/\n/g, '</p><p>')}</p>`,
      },
      setResponse: (res: any) => {
        setSendingCommission(false);
        if (res.status === 'success') {
          enqueueSnackbar(`Correo enviado a ${commissionDialogRow.email}`, { variant: 'success' });
          setCommissionDialogRow(null);
        } else {
          enqueueSnackbar(res.message || 'No se pudo enviar el correo', { variant: 'error' });
        }
      },
      onError: () => {
        setSendingCommission(false);
        enqueueSnackbar('No se pudo enviar el correo', { variant: 'error' });
      }
    });
  };

  const sendIncompleteNotices = () => {
    setSendingIncomplete(true);
    ApiRequest({
      url: '/kinesiologists/notify-incomplete',
      method: 'POST',
      timeout: 120_000,
      body: { subject: incompleteSubject, body: incompleteBody },
      setResponse: (res: any) => {
        setSendingIncomplete(false);
        setNotifyIncompleteOpen(false);
        if (res.status === 'success') {
          enqueueSnackbar(`Aviso enviado a ${res.sent} de ${res.total_targets} kinesiólogos con perfil incompleto.`, { variant: 'success' });
          setIncompleteResult({ sent: res.sent, total_targets: res.total_targets, failed: res.failed || [] });
        } else {
          enqueueSnackbar(res.message || 'No se pudo enviar el aviso masivo', { variant: 'error' });
        }
      },
      onError: () => {
        setSendingIncomplete(false);
        setNotifyIncompleteOpen(false);
        enqueueSnackbar('No se pudo enviar el aviso masivo', { variant: 'error' });
      }
    });
  };

  const filtered = list.filter(r => {
    const q = search.toLowerCase();
    const matchSearch = !q || `${r.name} ${r.lastname} ${r.email} ${r.region} ${r.home_comuna_label} ${r.locations_labels} ${r.skills_labels}`.toLowerCase().includes(q);
    const matchEstado = filterEstado === 'Todas' || r.estado === filterEstado;
    const matchTipo   = filterTipo === 'Todos'
      || (filterTipo === 'Domicilio' ? r.advanced_wound_care : filterTipo === 'Consulta' ? r.online_advice : true);
    const matchComision = filterComision === 'Todos'
      || (filterComision === 'Aceptada' ? r.commission_consent : !r.commission_consent);
    const matchResidencia = !filterResidencia || (r.home_comuna_label || '').toLowerCase().includes(filterResidencia.toLowerCase());
    const matchPerfil = filterPerfil === 'Todos'
      || (filterPerfil === 'Incompleto' ? r.profile_incomplete : !r.profile_incomplete);
    return matchSearch && matchEstado && matchTipo && matchComision && matchResidencia && matchPerfil;
  });

  const exportExcel = () => {
    const rows = filtered.map((r, i) => ({
      '#': i + 1,
      Estado: r.estado || '',
      Nombre: r.name,
      Apellido: r.lastname,
      Email: r.email,
      RUT: r.rut || '',
      Teléfono: r.phone,
      WhatsApp: r.whatsapp || '',
      Domicilio: r.advanced_wound_care ? 'Sí' : 'No',
      Consulta: r.online_advice ? 'Sí' : 'No',
      Región: r.region,
      'Comuna de Residencia': r.home_comuna_label || '',
      Comunas: r.locations_labels,
      Especialidades: r.skills_labels,
      'Datos Faltantes': (r.missing_profile_fields || []).length > 0 ? r.missing_profile_fields.join(', ') : 'Completo',
      Servicio: r.service || '',
      Precio: r.price || '',
      'Comisión $10.000': r.commission_consent ? 'Aceptada' : 'Pendiente',
      'Fecha Aceptación': r.commission_consent_date ? String(r.commission_consent_date).slice(0, 10) : '',
      'Fecha Registro': r.created_at ? new Date(r.created_at).toLocaleDateString('es-CL') : '',
      'Rating Promedio': r.rating_promedio,
      'N° Evaluaciones': r.rating_count,
      'Reg. Superintendencia': r.superintendencia_registro || '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [
      {wch:4},{wch:12},{wch:16},{wch:16},{wch:30},{wch:14},{wch:14},{wch:12},{wch:12},{wch:24},
      {wch:22},{wch:20},{wch:24},{wch:32},{wch:40},{wch:20},{wch:16},{wch:16},{wch:16},{wch:10},{wch:12},{wch:24}
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Kinesiólogos');
    XLSX.writeFile(wb, `kinesiologos_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  const cards = [
    { label: 'Total Registrados',       value: list.length,                                           color: '#005F6A' },
    { label: 'Activos',                 value: list.filter(r => r.estado === 'Activa').length,        color: '#15803d' },
    { label: 'Pendientes Aprobación',   value: list.filter(r => r.estado === 'Pendiente').length,     color: '#d97706' },
    { label: 'Rechazados',              value: list.filter(r => r.estado === 'Rechazada').length,     color: '#b91c1c' },
    { label: 'Inactivos',               value: list.filter(r => r.estado === 'Inactiva').length,      color: '#64748b' },
    { label: 'Perfil Incompleto',       value: list.filter(r => r.profile_incomplete).length,         color: '#ea580c' },
    { label: 'Comisión $10.000 Aceptada', value: list.filter(r => r.commission_consent).length,       color: '#9333ea' },
  ];

  const thStyle: any = {
    padding: '11px 14px', color: '#fff', fontWeight: 600,
    textAlign: 'left', fontSize: 12, whiteSpace: 'nowrap'
  };

  return (
    <>
      {/* Filters row */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 20 }}>
        <TextField
          size="small"
          placeholder="Buscar nombre, email, región..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ width: 260 }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Estado</InputLabel>
          <Select value={filterEstado} label="Estado" onChange={e => setFilterEstado(e.target.value)}>
            <MenuItem value="Todas">Todas</MenuItem>
            <MenuItem value="Activa">Activas</MenuItem>
            <MenuItem value="Pendiente">Pendientes de Aprobación</MenuItem>
            <MenuItem value="Rechazada">Rechazadas</MenuItem>
            <MenuItem value="Inactiva">Inactivas</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Tipo</InputLabel>
          <Select value={filterTipo} label="Tipo" onChange={e => setFilterTipo(e.target.value)}>
            <MenuItem value="Todos">Todos los tipos</MenuItem>
            <MenuItem value="Domicilio">Kinesiología a Domicilio</MenuItem>
            <MenuItem value="Consulta">Kinesiología en Consulta</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Perfil</InputLabel>
          <Select value={filterPerfil} label="Perfil" onChange={e => setFilterPerfil(e.target.value)}>
            <MenuItem value="Todos">Todos</MenuItem>
            <MenuItem value="Incompleto">Datos faltantes</MenuItem>
            <MenuItem value="Completo">Perfil completo</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Comisión</InputLabel>
          <Select value={filterComision} label="Comisión" onChange={e => setFilterComision(e.target.value)}>
            <MenuItem value="Todos">Todos</MenuItem>
            <MenuItem value="Aceptada">Aceptada</MenuItem>
            <MenuItem value="Pendiente">Pendiente</MenuItem>
          </Select>
        </FormControl>
        <TextField
          size="small"
          placeholder="Filtrar por residencia..."
          value={filterResidencia}
          onChange={e => setFilterResidencia(e.target.value)}
          sx={{ width: 200 }}
        />
        <Button
          variant="contained"
          startIcon={<MarkEmailReadOutlinedIcon />}
          onClick={() => setNotifyIncompleteOpen(true)}
          disabled={list.filter(r => r.profile_incomplete).length === 0}
          sx={{ ml: 'auto', background: '#ea580c', '&:hover': { background: '#c2410c' }, fontWeight: 600 }}
        >
          Avisar Perfiles Incompletos ({list.filter(r => r.profile_incomplete).length})
        </Button>
        <Button
          variant="contained"
          startIcon={<FileDownloadOutlinedIcon />}
          onClick={exportExcel}
          disabled={filtered.length === 0}
          sx={{ background: '#1d6f42', '&:hover': { background: '#155c36' }, fontWeight: 600 }}
        >
          Exportar Excel
        </Button>
      </div>

      {/* Dialog: confirmar aviso masivo a perfiles incompletos */}
      <Dialog open={notifyIncompleteOpen} onClose={() => !sendingIncomplete && setNotifyIncompleteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Enviar aviso de perfil incompleto</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Se enviará este correo a los <b>{list.filter(r => r.profile_incomplete).length}</b> kinesiólogos que en este momento tienen el perfil incompleto. Puedes editar el texto antes de enviar.
            <br />
            <span style={{ fontSize: 12, color: '#888' }}>
              <code>{'{{nombre}}'}</code> y <code>{'{{faltantes}}'}</code> se reemplazan automáticamente por los datos de cada kinesiólogo.
            </span>
          </DialogContentText>
          <TextField
            label="Asunto"
            fullWidth
            size="small"
            value={incompleteSubject}
            onChange={e => setIncompleteSubject(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Mensaje"
            fullWidth
            multiline
            minRows={7}
            value={incompleteBody}
            onChange={e => setIncompleteBody(e.target.value)}
          />
          <DialogContentText sx={{ mt: 2, fontSize: 12, color: '#888' }}>
            Si vuelves a usar este botón más adelante, solo se les enviará a quienes sigan incompletos en ese momento.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotifyIncompleteOpen(false)} disabled={sendingIncomplete}>Cancelar</Button>
          <Button onClick={sendIncompleteNotices} variant="contained" disabled={sendingIncomplete} sx={{ background: '#ea580c', '&:hover': { background: '#c2410c' } }}>
            {sendingIncomplete ? 'Enviando...' : 'Enviar aviso'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: resultado del envío masivo, incluyendo a quiénes no les llegó */}
      <Dialog open={!!incompleteResult} onClose={() => setIncompleteResult(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Resultado del envío</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Se enviaron correctamente <b>{incompleteResult?.sent}</b> de <b>{incompleteResult?.total_targets}</b> avisos.
          </DialogContentText>
          {incompleteResult && incompleteResult.failed.length > 0 && (
            <>
              <DialogContentText sx={{ color: '#b91c1c', fontWeight: 600, mb: 1 }}>
                No se pudieron enviar {incompleteResult.failed.length} correos (falla temporal del servidor de correo, no un dato inválido). Puedes reintentar más tarde usando "Avisar Perfiles Incompletos" de nuevo — solo se reenviará a quienes sigan incompletos:
              </DialogContentText>
              <div style={{ maxHeight: 200, overflowY: 'auto', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 12px' }}>
                {incompleteResult.failed.map((email, i) => (
                  <div key={i} style={{ fontSize: 13, color: '#7f1d1d', padding: '2px 0' }}>{email}</div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIncompleteResult(null)} variant="contained">Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: enviar aviso de comisión pendiente a un kinesiólogo puntual */}
      <Dialog open={!!commissionDialogRow} onClose={() => !sendingCommission && setCommissionDialogRow(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Avisar términos de comisión pendientes</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Para: <b>{commissionDialogRow?.name} {commissionDialogRow?.lastname}</b> ({commissionDialogRow?.email})
          </DialogContentText>
          <TextField
            label="Asunto"
            fullWidth
            size="small"
            value={commissionSubject}
            onChange={e => setCommissionSubject(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Mensaje"
            fullWidth
            multiline
            minRows={6}
            value={commissionBody}
            onChange={e => setCommissionBody(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommissionDialogRow(null)} disabled={sendingCommission}>Cancelar</Button>
          <Button onClick={sendCommissionNotice} variant="contained" disabled={sendingCommission}>
            {sendingCommission ? 'Enviando...' : 'Enviar correo'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(155px, 1fr))', gap: 12, marginBottom: 20 }}>
        {cards.map(c => <SummaryCard key={c.label} {...c} />)}
      </div>

      {/* Count */}
      <div style={{ fontSize: 13, color: '#888', marginBottom: 10 }}>
        Mostrando <b>{filtered.length}</b> de {list.length} kinesiólogos
      </div>

      {/* Table */}
      {fetching ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><CircularProgress /></div>
      ) : (
        <div style={{ overflowX: 'auto', borderRadius: 12, boxShadow: '0 1px 8px rgba(0,0,0,0.08)', background: '#fff' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#005F6A' }}>
                {['#','Estado','Nombre','Contacto','Tipo','Residencia','Región / Comunas Servicio','Especialidades','Perfil','Comisión $10.000','Registro','Rating'].map(h => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={12} style={{ textAlign: 'center', padding: 40, color: '#aaa' }}>Sin resultados</td></tr>
              )}
              {filtered.map((row, i) => {
                const est   = estadoChip(row.estado || 'Inactiva');
                const bg    = i % 2 === 0 ? '#fff' : '#f7faff';
                return (
                  <tr key={row.user_id}
                    style={{ background: bg, borderBottom: '1px solid #eef1f6' }}
                    onMouseEnter={e => (e.currentTarget.style.background = '#edf4ff')}
                    onMouseLeave={e => (e.currentTarget.style.background = bg)}
                  >
                    <td style={{ padding: '10px 14px', color: '#bbb', fontWeight: 600 }}>{i + 1}</td>

                    <td style={{ padding: '10px 14px' }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        background: est.bg, color: est.color,
                        padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600
                      }}>
                        {est.icon}{row.estado || 'Inactiva'}
                      </span>
                    </td>

                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                      <div style={{ fontWeight: 600, color: '#1a1a2e' }}>{row.name} {row.lastname}</div>
                      <div style={{ fontSize: 11, color: '#aaa' }}>ID: {row.user_id}</div>
                    </td>

                    <td style={{ padding: '10px 14px' }}>
                      <div style={{ color: '#444' }}>{row.email}</div>
                      <div style={{ fontSize: 11, color: '#888' }}>
                        {row.phone}{row.whatsapp ? ` · WA: ${row.whatsapp}` : ''}
                      </div>
                    </td>

                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                      {row.advanced_wound_care && (
                        <Chip label="Domicilio" size="small" sx={{ background: '#e3f0ff', color: '#005F6A', fontWeight: 600, fontSize: 11, mr: 0.5 }} />
                      )}
                      {row.online_advice && (
                        <Chip label="Consulta" size="small" sx={{ background: '#e8f5e9', color: '#2e7d32', fontWeight: 600, fontSize: 11 }} />
                      )}
                      {!row.advanced_wound_care && !row.online_advice && <span style={{ color: '#cbd5e1' }}>—</span>}
                    </td>

                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                      <span style={{ fontSize: 13, color: '#475569' }}>
                        {row.home_comuna_label || <span style={{ color: '#cbd5e1' }}>—</span>}
                      </span>
                    </td>

                    <td style={{ padding: '10px 14px', maxWidth: 180 }}>
                      <div style={{ fontWeight: 500, color: '#333' }}>{row.region}</div>
                      <Tooltip title={row.locations_labels || ''} placement="top">
                        <div style={{ fontSize: 11, color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160 }}>
                          {row.locations_labels || '—'}
                        </div>
                      </Tooltip>
                    </td>

                    <td style={{ padding: '10px 14px', maxWidth: 200 }}>
                      <Tooltip title={row.skills_labels || ''} placement="top">
                        <div style={{ fontSize: 12, color: '#555', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180 }}>
                          {row.skills_labels || '—'}
                        </div>
                      </Tooltip>
                    </td>

                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                      {row.profile_incomplete ? (
                        <Tooltip title={`Le falta: ${(row.missing_profile_fields || []).join(', ')}`} placement="top">
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            background: '#ffedd5', color: '#c2410c',
                            padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: 'help'
                          }}>
                            <WarningAmberIcon sx={{ fontSize: 14 }} /> Datos faltantes
                          </span>
                        </Tooltip>
                      ) : (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#dcfce7', color: '#15803d', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>
                          <CheckCircleOutlineIcon sx={{ fontSize: 14 }} /> Completo
                        </span>
                      )}
                    </td>

                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                      {row.commission_consent ? (
                        <span style={{ background: '#dbeafe', color: '#1d4ed8', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>Aceptada</span>
                      ) : (
                        <Tooltip title="Aún no acepta los términos — clic para enviarle un correo" placement="top">
                          <span
                            onClick={() => openCommissionDialog(row)}
                            style={{ background: '#f1f5f9', color: '#64748b', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
                          >
                            Aún no acepta ✉
                          </span>
                        </Tooltip>
                      )}
                      {row.commission_consent_date && (
                        <div style={{ fontSize: 10, color: '#aaa', marginTop: 3 }}>
                          {String(row.commission_consent_date).slice(0, 10)}
                        </div>
                      )}
                    </td>

                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                      {row.created_at ? (
                        <span style={{ fontSize: 12, color: '#475569' }}>
                          {new Date(row.created_at).toLocaleDateString('es-CL')}
                        </span>
                      ) : (
                        <span style={{ color: '#cbd5e1' }}>—</span>
                      )}
                    </td>

                    <td style={{ padding: '10px 14px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <div style={{ fontWeight: 700, color: Number(row.rating_promedio) > 0 ? '#f59e0b' : '#ccc', fontSize: 15 }}>
                        {Number(row.rating_promedio) > 0 ? `★ ${row.rating_promedio}` : '—'}
                      </div>
                      {row.rating_count > 0 && <div style={{ fontSize: 10, color: '#aaa' }}>{row.rating_count} eval.</div>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ─── Patients tab ─────────────────────────────────────────────────────────────
function PatientsTab() {
  const [list, setList]         = useState<any[]>([]);
  const [fetching, setFetching] = useState(true);
  const [search, setSearch]     = useState('');
  const [filterEstado, setFilterEstado] = useState('Todos');

  useEffect(() => {
    ApiRequest({
      url: '/users/list',
      method: 'GET',
      query: { full_list: 1 },
      setResponse: (res: any) => {
        const patients = (res.list || []).filter((u: any) => u.user_type === 'Paciente');
        setList(patients);
        setFetching(false);
      }
    });
  }, []);

  const filtered = list.filter(r => {
    const q = search.toLowerCase();
    const matchSearch = !q || `${r.name} ${r.lastname} ${r.email} ${r.phone}`.toLowerCase().includes(q);
    const matchEstado = filterEstado === 'Todos' || (filterEstado === 'Activo' ? r.is_active : !r.is_active);
    return matchSearch && matchEstado;
  });

  const exportExcel = () => {
    const rows = filtered.map((r, i) => ({
      '#': i + 1,
      Nombre: r.name,
      Apellido: r.lastname,
      Email: r.email,
      Teléfono: r.phone,
      Activo: r.is_active ? 'Sí' : 'No',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [{wch:4},{wch:16},{wch:16},{wch:30},{wch:16},{wch:8}];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Pacientes');
    XLSX.writeFile(wb, `pacientes_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  const cards = [
    { label: 'Total Pacientes',    value: list.length,                             color: '#005F6A' },
    { label: 'Activos',            value: list.filter(r => r.is_active).length,    color: '#15803d' },
  ];

  const thStyle: any = {
    padding: '11px 14px', color: '#fff', fontWeight: 600,
    textAlign: 'left', fontSize: 12, whiteSpace: 'nowrap'
  };

  return (
    <>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 20 }}>
        <TextField
          size="small"
          placeholder="Buscar nombre, email, teléfono..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ width: 260 }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Estado</InputLabel>
          <Select value={filterEstado} label="Estado" onChange={e => setFilterEstado(e.target.value)}>
            <MenuItem value="Todos">Todos</MenuItem>
            <MenuItem value="Activo">Activos</MenuItem>
            <MenuItem value="Inactivo">Inactivos</MenuItem>
          </Select>
        </FormControl>
        <Button
          variant="contained"
          startIcon={<FileDownloadOutlinedIcon />}
          onClick={exportExcel}
          disabled={filtered.length === 0}
          sx={{ ml: 'auto', background: '#1d6f42', '&:hover': { background: '#155c36' }, fontWeight: 600 }}
        >
          Exportar Excel
        </Button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(155px, 1fr))', gap: 12, marginBottom: 20 }}>
        {cards.map(c => <SummaryCard key={c.label} {...c} />)}
      </div>

      <div style={{ fontSize: 13, color: '#888', marginBottom: 10 }}>
        Mostrando <b>{filtered.length}</b> de {list.length} pacientes
      </div>

      {fetching ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><CircularProgress /></div>
      ) : (
        <div style={{ overflowX: 'auto', borderRadius: 12, boxShadow: '0 1px 8px rgba(0,0,0,0.08)', background: '#fff' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#005F6A' }}>
                {['#','Nombre','Email','Teléfono','Estado'].map(h => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={5} style={{ textAlign: 'center', padding: 40, color: '#aaa' }}>Sin resultados</td></tr>
              )}
              {filtered.map((row, i) => {
                const bg = i % 2 === 0 ? '#fff' : '#f7faff';
                return (
                  <tr key={row.user_id}
                    style={{ background: bg, borderBottom: '1px solid #eef1f6' }}
                    onMouseEnter={e => (e.currentTarget.style.background = '#edf4ff')}
                    onMouseLeave={e => (e.currentTarget.style.background = bg)}
                  >
                    <td style={{ padding: '10px 14px', color: '#bbb', fontWeight: 600 }}>{i + 1}</td>

                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                      <div style={{ fontWeight: 600, color: '#1a1a2e' }}>{row.name} {row.lastname}</div>
                      <div style={{ fontSize: 11, color: '#aaa' }}>ID: {row.user_id}</div>
                    </td>

                    <td style={{ padding: '10px 14px', color: '#444' }}>{row.email}</td>

                    <td style={{ padding: '10px 14px', color: '#555', whiteSpace: 'nowrap' }}>{row.phone || '—'}</td>

                    <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                      {row.is_active
                        ? <CheckCircleOutlineIcon sx={{ color: '#15803d', fontSize: 20 }} />
                        : <CancelOutlinedIcon sx={{ color: '#d32f2f', fontSize: 20 }} />
                      }
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function NursesReport(props: any) {
  const { setLoading } = props;
  const [tab, setTab] = useState(0);

  useEffect(() => {
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="content" style={{ padding: '24px' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: '0 0 4px 0', fontSize: 22, fontWeight: 700, color: '#005F6A' }}>
          Gestión de Kinesiólogos
        </h1>
        <span style={{ color: '#888', fontSize: 13 }}>
          Directorio de kinesiólogos y pacientes registrados en la plataforma
        </span>
      </div>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ borderBottom: '1px solid #e5e7eb', marginBottom: 24 }}
        TabIndicatorProps={{ style: { background: '#005F6A' } }}
      >
        <Tab label="Kinesiólogos" sx={{ fontWeight: 600, textTransform: 'none', fontSize: 14 }} />
        <Tab label="Pacientes" sx={{ fontWeight: 600, textTransform: 'none', fontSize: 14 }} />
      </Tabs>

      {tab === 0 && <KinesiologistsTab />}
      {tab === 1 && <PatientsTab />}
    </div>
  );
}
